import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { raceClassFeeText } from '../../lib/raceClassFee';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import ImageScroller from '../../components/imagescroller';
import InspectionStatus from '../../components/inspection/status';
import GuiList from '../../components/gui/list';
import '../../components/gui/tileList.css';

function displayImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3MediumLink}`;
    return imgSrc;
}

// Shared look for every pill on a race class row (same as race/list.js).
const PILL = 'badge rounded-pill me-1';
const PILL_STYLE = 'font-size: 0.7rem; font-weight: 500;';

// Mirrors isSignupOpen in backend/lib/signup-visibility.js: signupStartDate
// must be set and passed, signupEndDate unset or still ahead.
function isSignupOpen(raceClass, now = new Date()) {
    if (!raceClass || !raceClass.signupStartDate) {
        return false;
    }
    const start = new Date(raceClass.signupStartDate);
    if (Number.isNaN(start.getTime()) || start > now) {
        return false;
    }
    if (raceClass.signupEndDate) {
        const end = new Date(raceClass.signupEndDate);
        if (!Number.isNaN(end.getTime()) && end < now) {
            return false;
        }
    }
    return true;
}

@withText(props => ({
    titleText: <Text id='race-class.title'>Race Classes</Text>,
    signupsText: <Text id='race.signups'>signups</Text>,
    signupOpenText: <Text id='race.signup-open'>Signup open</Text>,
}))
@observer
class RaceClassList extends Component {
  	constructor(props) {
        super(props);
        this.state = {
        };
    }

    viewRaceClass = e => {
        const { view } = this.props;
        if (view) {
            return view(e);
        }
        const { id, title } = e.target.closest('.box-line').dataset;
        route(`/raceclass/${id}`);
    }

    // One row of pills instead of the old muted text (unreadable in dark mode):
    // fee, signups, «Påmelding åpen» and free spots / full. The class start
    // time is not shown here (2026-10-02); the class detail page has it.
    renderPills = e => {
        const { signupOpenText } = this.props;
        const fee = raceClassFeeText(e);
        const signupOpen = isSignupOpen(e);
        const hasLimit = e.maxMushers > 0;
        const freeSpots = hasLimit ? Math.max(0, e.maxMushers - (e.totalContestants || 0)) : null;
        return (<>
            {fee ? (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE}>
                    <i class='fa-duotone fa-money-bill me-1' />{fee}
                </span>
            ) : ''}
            {e.totalContestants > 0 && (
                <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Påmeldte'>
                    <i class='fa-duotone fa-user me-1' />{e.totalContestants}{hasLimit ? `/${e.maxMushers}` : ''}
                </span>
            )}
            {signupOpen ? (
                <span class={`${PILL} text-bg-success`} style={PILL_STYLE} title={signupOpenText}>
                    <i class='fa-duotone fa-user-plus me-1' />{signupOpenText}
                </span>
            ) : ''}
            {signupOpen && hasLimit && freeSpots > 0 ? (
                <span class={`${PILL} bg-success-subtle text-success-emphasis border border-success-subtle`} style={PILL_STYLE}>
                    <Text id='race.free-spots-short' fields={{ count: freeSpots }}>{freeSpots} free</Text>
                </span>
            ) : ''}
            {signupOpen && hasLimit && freeSpots === 0 ? (
                <span class={`${PILL} text-bg-danger`} style={PILL_STYLE}>
                    <Text id='race.full'>Full</Text>
                </span>
            ) : ''}
        </>);
    }

    render() {
        const { appState, userStore, raceStore } = this.props.stores;
        const { isDevelopment, path, apiServer } = appState;
        const { user } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { titleText, signupsText } = this.props;
        const {
            raceClasses,
            classNames = '',
            hideEmpty,
            title = titleText,
            editElement,
            showCevron,

            rightBoxField = 'distance',
            rightBoxFieldValue = e => e,
            rightBoxFieldPostfix = 'km',
            rightBoxFieldPostfixField = 'startDate',
            rightBoxFieldPostfixValue = e => util.formatDate(e, { year: 'numeric', locale: 'nb-NO' }, true),
            help,
        } = this.props;

        return (<>
            <GuiList
                stores={this.props.stores}
                holdingStore={raceStore}
                view={this.viewRaceClass}
                classNames={classNames}
                classNamesList={'mx-3 tile-list pill-list'}
                title={title}
                list={raceClasses}
                elTitle={e => e.title}
                elInfo1={this.renderPills}
                elInfo2={e => ''}
                elInfo3={e => ''}
                hideEmpty={hideEmpty}
                editElement={editElement}
                showEmptyImage={false}
                showCevron={showCevron}
                showFilterBox={false}
                rightBoxField={rightBoxField}
                rightBoxFieldValue={rightBoxFieldValue}
                rightBoxFieldPostfix={rightBoxFieldPostfix}
                help={help}
            />
        </>);
    }
}

export default RaceClassList;
